<script setup lang="ts">
    import { computed } from 'vue';
    import { collectInputConfig, FormProcessState } from '~/utils/formUtils';
    import { useFormHandler } from '~/composables/useForm';

    const props = defineProps<{ blok: FormGroupStoryblok; uuid?: string }>();
    const { t } = useI18n();

    const isPending = ref(false);

    const inputConfig = computed(() => collectInputConfig(props.blok.formElements));

    const hasFileUpload = computed(() => inputConfig.value.some((cfg) => cfg.component === 'FormUpload'));

    const captchaRef = useTemplateRef<HTMLDivElement>('captchaRef');

    const { formRef, formState, hasRequiredFields, alignmentClass, errorCount, hasErrors, submitForm } = useFormHandler(
        props.blok,
        inputConfig,
        captchaRef
    );

    const isCaptchaComplete = computed(() => {
        return !!formState.value.formInputs['frc-captcha-response']?.value;
    });

    const handleSubmit = async () => {
        isPending.value = true;
        await submitForm(props.uuid);

        if (hasErrors.value) {
            isPending.value = false;
            const firstError = formRef.value?.querySelector('.--has-error');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        // fallback for failed HTTP submissions that don't trigger an error state
        setTimeout(() => {
            if (document.visibilityState === 'visible') {
                isPending.value = false;
            }
        }, 3000);
    };

    onMounted(() => {
        // focus first input if in a modal
        nextTick(() => {
            const selector = formRef.value;
            if (!selector || !selector.closest('.modal')) return;

            const input = selector.querySelector<HTMLElement>(
                `
                input:not([hidden]):not([disabled]),
                select:not([hidden]):not([disabled]),
                textarea:not([hidden]):not([disabled]),
                [tabindex]:not([hidden]):not([disabled]):not([tabindex="-1"])
                `
            );

            if (input) {
                const style = window.getComputedStyle(input);
                const isVisible =
                    style.display !== 'none' &&
                    style.visibility !== 'hidden' &&
                    style.opacity !== '0' &&
                    input.offsetParent !== null;

                if (isVisible) {
                    input.focus();
                }
            }
        });
    });
</script>

<template>
    <div
        v-if="formState.processState === FormProcessState.DEFAULT"
        v-editable="props.blok"
        class="form-wrapper"
        :class="{ '--labels-outside': props.blok.labelPosition === 'outside' }"
    >
        <template v-if="props.blok.formHeader">
            <StoryblokComponentWrapper
                v-for="formHeaderComponent in props.blok.formHeader"
                :key="formHeaderComponent._uid"
                :blok="formHeaderComponent"
            />
        </template>
        <h2 v-if="blok.title">{{ blok.title }}</h2>
        <form
            ref="formRef"
            class="input-wrapper"
            :action="props.blok.submitAddress"
            :method="props.blok.method === 'POST' ? 'POST' : 'GET'"
            :enctype="hasFileUpload ? 'multipart/form-data' : undefined"
            novalidate
            @submit.prevent="handleSubmit"
        >
            <StoryblokComponentWrapper
                v-for="formElement in props.blok.formElements"
                :key="formElement._uid"
                :blok="formElement"
            />
            <p
                v-if="hasRequiredFields && !props.blok.hideRequiredText"
                class="required-message"
                :class="alignmentClass"
            >
                <span>*</span> {{ t('formElement.required') }}
            </p>

            <div
                class="captcha-btn-wrapper"
                :class="alignmentClass"
            >
                <div
                    v-if="blok.useFriendlyCaptcha"
                    ref="captchaRef"
                ></div>
            </div>

            <div
                class="submit-btn-wrapper"
                :class="alignmentClass"
            >
                <PrimaryButton
                    :button-text="props.blok.submitButtonText"
                    type="submit"
                    :native-button="true"
                    size="medium"
                    :is-pending="isPending"
                    :disabled="blok.useFriendlyCaptcha && !isCaptchaComplete"
                />
            </div>
        </form>
    </div>

    <div v-if="formState.processState === FormProcessState.SUCCESS">
        <StoryblokComponentWrapper
            v-for="component in props.blok.successComponent"
            :key="component._uid"
            :blok="component"
        />
    </div>
    <div v-if="formState.processState === FormProcessState.ERROR">
        <StoryblokComponentWrapper
            v-for="component in props.blok.errorComponent"
            :key="component._uid"
            :blok="component"
        />
    </div>
    <div v-if="formState.processState === FormProcessState.INVALID">
        <h1>
            {{ t('formElement.formInvalid') }}
        </h1>
    </div>

    <div
        class="sr-only"
        aria-live="assertive"
    >
        <span v-if="hasErrors">
            {{ t('formElement.errorCountMessage', { count: errorCount }) }}
        </span>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/form/form-group.scss';
</style>
